import {ReactNode, useCallback, useMemo, useState} from "react";
import {Page} from "@/components/pages";
import Loading from "../breadcrumbs/Loading";
import {useDataSource} from "@/components/special-days/DataSource";
import SpecialDayPicker, {sameDate, sameMonthAndDay} from "../SpecialDayPicker";
import {OverrideDay, SpecialDay} from "@/api";
import {defineMessage, FormattedMessage, useIntl} from "react-intl";
import {Dialog, DialogActions, Typography} from "@mui/material";
import {DateTime} from "luxon";
import {Box} from "@mui/system";
import {useUpdateSpecialDaysMutation} from "./mutations.generated";
import {showNotification} from "@/actions";
import {useDispatch} from "react-redux";
import AddSpecialDay from "./AddSpecialDay";
import {
    FormActionButtons,
    FormattedButton,
    FormattedDialogTitle,
    IconDialogContent,
    msg_cancel,
} from "@dvrt/graphql-widgets";
import {msg_remove, WEEKDAY_MESSAGES} from "@/utils/translations";

/** e.g. Jun 19 */
const DATE_MED_WITHOUT_YEAR = {month: "short", day: "numeric"} as const;

const getSpecialDayFilter = (target: DateTime) => (specialDay: SpecialDay) => {
    const specialDayDate = DateTime.fromISO(specialDay.day);
    if (specialDay.everyYear) return sameMonthAndDay(target, specialDayDate);
    else return sameDate(target, specialDayDate);
};

const formatDate = (date: DateTime, everyYear?: boolean): string => {
    const dateFormat = everyYear ? DATE_MED_WITHOUT_YEAR : DateTime.DATE_MED;
    return date.toLocaleString(dateFormat);
};

type DateSelectorValue = {
    /** The real value of the date, used for the form value */
    realValue: Date;

    /** When selecting a special day that occurs every year, we might display the future/past date
     * instead of the real value (which was saved in the database), so that it is not confusing for the user. */
    displayValue?: Date;
};

const useDateSelector = (initialDate: Date) => {
    const [dates, setDates] = useState<DateSelectorValue>({
        realValue: initialDate,
        displayValue: undefined,
    });

    const setDate = useCallback((newDate: Date, displayValue?: Date) => {
        setDates({realValue: newDate, displayValue});
    }, []);

    return {
        date: dates.realValue,
        displayDate: dates.displayValue,
        setDate,
    };
};

type RemoveDayConfirmDialogProps = {
    formattedDate: string;
    open: boolean;
    onConfirm: () => void;
    onClose: () => void;
    removing: boolean;
};

const RemoveDayConfirmDialog = ({formattedDate, open, onConfirm, onClose, removing}: RemoveDayConfirmDialogProps) => {
    return (
        <Dialog open={open}>
            <FormattedDialogTitle
                title={defineMessage({id: "specialDays.remove.title", defaultMessage: "Remove override for {date}"})}
                values={{date: formattedDate}}
            />
            <IconDialogContent icon="question-circle">
                <FormattedMessage
                    id="specialDays.remove.content"
                    defaultMessage="Are you sure you want to remove the override for {date}?"
                    values={{date: formattedDate}}
                />
            </IconDialogContent>
            <DialogActions>
                <FormActionButtons
                    mainButtonText={msg_remove}
                    secondaryButtonText={msg_cancel}
                    mainAction={() => {
                        onConfirm();
                        onClose();
                    }}
                    secondaryAction={onClose}
                    variant="text"
                    mainButtonColor="secondary"
                    pending={removing}
                />
            </DialogActions>
        </Dialog>
    );
};

type SelectedDateProps = {
    selectedDate: Date;
    specialDays: SpecialDay[];
    refetch: () => void;
};

const SelectedDateTitle = ({children}: {children: ReactNode}) => (
    <Typography variant="h5" fontStyle="normal">
        {children}
    </Typography>
);

const SelectedDate = ({selectedDate, specialDays, refetch}: SelectedDateProps) => {
    const {formatMessage} = useIntl();
    const dispatch = useDispatch();
    const [updateSpecialDays] = useUpdateSpecialDaysMutation();
    const [removing, setRemoving] = useState(false);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const specialDay = useMemo(
        () => specialDays.find(getSpecialDayFilter(DateTime.fromJSDate(selectedDate))),
        [selectedDate, specialDays],
    );

    const dateString = useMemo(() => {
        return formatDate(DateTime.fromJSDate(selectedDate), specialDay?.everyYear);
    }, [selectedDate, specialDay]);

    // helper function to handle errors and refetching
    const mutate = useCallback(
        async (days: OverrideDay[], onSuccess: () => void) => {
            const result = await updateSpecialDays({
                variables: {
                    days,
                },
            });

            const errors = result.errors ?? result.data.updateSpecialDays.errors;
            if (errors) {
                console.error("Error updating special days:", errors);
                dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
            } else {
                onSuccess();
                refetch();
            }
        },
        [dispatch, refetch, updateSpecialDays],
    );

    const onAddSpecialDay = useCallback(
        async (specialDay: OverrideDay) => {
            const newDays = [...(specialDays as OverrideDay[]), specialDay];
            await mutate(newDays, () => {
                const date = formatDate(DateTime.fromISO(specialDay.day), specialDay.everyYear);
                const day = formatMessage(WEEKDAY_MESSAGES[specialDay.considerAs]);
                dispatch(
                    showNotification(
                        defineMessage({
                            id: "specialDays.add.success",
                            defaultMessage: "Successfully overridden {date} with {day}",
                        }),
                        "success",
                        {date, day},
                    ),
                );
            });
        },
        [specialDays, mutate, formatMessage, dispatch],
    );

    const onRemoveSpecialDay = useCallback(
        async (specialDay: SpecialDay) => {
            setRemoving(true);

            const newDays = structuredClone(specialDays);
            newDays.splice(specialDays.indexOf(specialDay), 1);

            await mutate(newDays, () => {
                const date = formatDate(DateTime.fromISO(specialDay.day), specialDay.everyYear);
                dispatch(
                    showNotification(
                        defineMessage({
                            id: "specialDays.remove.success",
                            defaultMessage: "Override for {date} has been successfully removed.",
                        }),
                        "success",
                        {date},
                    ),
                );
            });

            setRemoving(false);
        },
        [specialDays, mutate, dispatch],
    );

    if (specialDay) {
        const translatedWeekday = formatMessage(WEEKDAY_MESSAGES[specialDay.considerAs]);

        return (
            <Box>
                <SelectedDateTitle>
                    <FormattedMessage
                        id="specialDays.display.override"
                        defaultMessage="{date} overriden with {day}"
                        values={{day: translatedWeekday, date: dateString}}
                    />
                </SelectedDateTitle>
                <FormattedButton
                    message={defineMessage({id: "remove", defaultMessage: "Remove"})}
                    onClick={() => setShowRemoveConfirm(true)}
                    color="secondary"
                />
                <RemoveDayConfirmDialog
                    formattedDate={dateString}
                    open={showRemoveConfirm}
                    onConfirm={() => onRemoveSpecialDay(specialDay)}
                    onClose={() => setShowRemoveConfirm(false)}
                    removing={removing}
                />
            </Box>
        );
    }

    return (
        <Box>
            <SelectedDateTitle>
                <FormattedMessage
                    id="specialDays.display.noOverride"
                    defaultMessage="No override for {date}"
                    values={{date: dateString}}
                />
            </SelectedDateTitle>
            <AddSpecialDay date={selectedDate} onSave={onAddSpecialDay} />
        </Box>
    );
};

const Content = ({specialDays, refetch}: {specialDays: SpecialDay[]; refetch: () => void}) => {
    const {date, setDate} = useDateSelector(new Date());

    return (
        <Page title={defineMessage({id: "specialDays.label", defaultMessage: "Special days"})} dividerMargin="none">
            <div style={{display: "flex"}}>
                <SpecialDayPicker
                    value={date}
                    onChange={(selectedDate) => setDate(selectedDate)}
                    specialDays={specialDays.map((d) => ({
                        day: d.day,
                        everyYear: d.everyYear,
                    }))}
                    sx={{margin: 0}}
                    showEveryYear
                />
                <Box sx={{margin: 2}}>
                    <SelectedDate selectedDate={date} specialDays={specialDays} refetch={refetch} />
                </Box>
            </div>
        </Page>
    );
};

const SpecialDays = () => {
    const dataSource = useDataSource();

    if (dataSource.loading) return <Loading />;

    return <Content specialDays={dataSource.days} refetch={dataSource.refetch} />;
};

export default SpecialDays;
